import type { EmbeddingProfileId } from '../data/embeddingProfiles'
import {
  ROUTE_CARDS,
  type RouteCard,
} from '../data/routeCards'
import type { SemanticMatch } from '../domain/types'
import {
  aggregatePieceTierScores,
  decideSemanticRoute,
  filterSemanticRoutingRanking,
  FROZEN_SEMANTIC_MIXTURE_POLICY,
  mixTierScores,
  splitPromptForSemanticRouting,
  tierScoresFromRanking,
  type SemanticRouteDecision,
  type SemanticRankingEntry,
} from '../engine/semanticRouting'
import {
  decodeVectorMatrix,
  searchVectorMatrix,
} from '../engine/vectorMatrix'
import type {
  EmbeddingErrorCode,
  SetupProgress,
  WorkerRequest,
  WorkerResponse,
} from './protocol'
import { classifyEmbeddingFailure } from './fallbackPolicy'

type ArtifactManifest = {
  schemaVersion: number
  profileId: EmbeddingProfileId
  dimensions: number
  routeCardIds: string[]
  vectorSha256: string
  vectorUrl: string
}

export type EmbeddingClientEvent =
  | {
      type: 'progress'
      progress: Omit<SetupProgress, 'status'> & {
        status: SetupProgress['status'] | 'ready'
        file?: string
      }
    }
  | {
      type: 'ready'
      device: string
    }
  | {
      type: 'fallback'
      failedDevice: string
      nextDevice: string
    }

type PendingRequest = {
  resolve: (result: RawEmbedding) => void
  reject: (error: Error) => void
}

type PendingLoad = {
  loadId: string
  resolve: (result: EmbeddingReady) => void
  reject: (error: Error) => void
}

export type EmbeddingReady = {
  device: string
}

export type EmbeddingResult = {
  device: string
  matches: SemanticMatch[]
  routeDecision: SemanticRouteDecision
}

type RawEmbedding = {
  device: string
  vector: Float32Array
}

export type EmbeddingWorkerFactory = (
  profileId: EmbeddingProfileId,
) => Worker

function defaultWorkerFactory(
  profileId: EmbeddingProfileId,
): Worker {
  return new Worker(
    new URL('../workers/embedding.worker.ts', import.meta.url),
    {
      type: 'module',
      name: `samrouter-${profileId}`,
    },
  )
}

function manifestUrl(profileId: EmbeddingProfileId): string {
  return `/artifacts/${profileId === 'embeddinggemma' ? 'embeddinggemma' : 'snowflake-xs'}.manifest.json`
}

const ROUTE_CARDS_BY_ID: ReadonlyMap<string, RouteCard> =
  new Map(ROUTE_CARDS.map((card) => [card.id, card]))

async function sha256Hex(value: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', value)
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('')
}

export class EmbeddingClientError extends Error {
  readonly code: EmbeddingErrorCode
  readonly phase: 'load' | 'inference'
  readonly retryable: boolean

  constructor(options: {
    code: EmbeddingErrorCode
    phase: 'load' | 'inference'
    retryable: boolean
    message: string
  }) {
    super(options.message)
    this.name = 'EmbeddingClientError'
    this.code = options.code
    this.phase = options.phase
    this.retryable = options.retryable
  }
}

function cancelledError(
  message: string,
  phase: 'load' | 'inference',
): EmbeddingClientError {
  return new EmbeddingClientError({
    code: 'CANCELLED',
    phase,
    retryable: true,
    message,
  })
}

export class EmbeddingClient {
  readonly profileId: EmbeddingProfileId

  private worker: Worker | null = null
  private readonly onEvent: (
    event: EmbeddingClientEvent,
  ) => void
  private readonly workerFactory: EmbeddingWorkerFactory
  private readonly pending = new Map<string, PendingRequest>()
  private pendingLoad: PendingLoad | null = null
  private loadPromise: Promise<EmbeddingReady> | null = null
  private ready: EmbeddingReady | null = null
  private artifactAbortController: AbortController | null = null
  private matrixPromise:
    | Promise<{
        manifest: ArtifactManifest
        matrix: ReturnType<typeof decodeVectorMatrix>
      }>
    | null = null
  private disposed = false

  constructor(
    profileId: EmbeddingProfileId,
    onEvent: (event: EmbeddingClientEvent) => void,
    workerFactory: EmbeddingWorkerFactory = defaultWorkerFactory,
  ) {
    this.profileId = profileId
    this.onEvent = onEvent
    this.workerFactory = workerFactory
  }

  async preload(): Promise<EmbeddingReady> {
    this.assertOpen()
    if (this.ready) return this.ready
    if (this.loadPromise) return this.loadPromise

    const worker = this.ensureWorker()
    const loadId = crypto.randomUUID()
    const promise = new Promise<EmbeddingReady>(
      (resolve, reject) => {
        this.pendingLoad = {
          loadId,
          resolve,
          reject,
        }
        this.post(worker, {
          type: 'load',
          loadId,
          profileId: this.profileId,
        })
      },
    )
    this.loadPromise = promise

    try {
      return await promise
    } catch (error) {
      if (this.loadPromise === promise) {
        this.loadPromise = null
      }
      throw error
    }
  }

  async compare(prompt: string): Promise<EmbeddingResult> {
    try {
      this.assertOpen()
      await this.preload()
      this.assertOpen()

      const worker = this.ensureWorker()
      const artifact = await this.loadMatrix()
      const wholeEmbedding = await this.embedText(
        worker,
        prompt,
      )
      const wholeRanking = this.searchFullMatrix(
        artifact,
        wholeEmbedding.vector,
      )
      const wholeTierScores = tierScoresFromRanking(
        filterSemanticRoutingRanking(
          wholeRanking,
          ROUTE_CARDS_BY_ID,
        ),
      )
      const pieceTierScores = []
      let device = wholeEmbedding.device

      for (const [
        pieceIndex,
        piece,
      ] of splitPromptForSemanticRouting(prompt).entries()) {
        const pieceEmbedding = await this.embedText(
          worker,
          piece,
        )
        device = pieceEmbedding.device
        const pieceRanking = this.searchFullMatrix(
          artifact,
          pieceEmbedding.vector,
        )
        pieceTierScores.push(
          tierScoresFromRanking(
            filterSemanticRoutingRanking(
              pieceRanking,
              ROUTE_CARDS_BY_ID,
            ),
            pieceIndex,
          ),
        )
      }

      const aggregatePieceScores =
        aggregatePieceTierScores(pieceTierScores)
      const mixedTierScores = mixTierScores(
        wholeTierScores,
        aggregatePieceScores,
        FROZEN_SEMANTIC_MIXTURE_POLICY.wholeWeight,
      )
      const routeDecision = decideSemanticRoute(
        mixedTierScores,
        FROZEN_SEMANTIC_MIXTURE_POLICY.thresholds,
      )

      return {
        device,
        matches: wholeRanking
          .slice(0, 5)
          .map(({ id, similarity }) => ({
          routeCardId: id,
          similarity,
        })),
        routeDecision,
      }
    } catch (error) {
      if (error instanceof EmbeddingClientError) {
        throw error
      }
      const failure = classifyEmbeddingFailure(
        error,
        'inference',
      )
      throw new EmbeddingClientError({
        code: failure.code,
        phase: 'inference',
        retryable: failure.retryable,
        message: failure.message,
      })
    }
  }

  pause(reason = 'Local analysis was paused.'): void {
    if (this.disposed) return
    this.stopWorker(reason)
    this.resetArtifacts()
  }

  dispose(reason = 'Local analysis was cancelled.'): void {
    if (this.disposed) return
    this.disposed = true
    this.stopWorker(reason)
    this.resetArtifacts()
  }

  private assertOpen(): void {
    if (this.disposed) {
      throw cancelledError(
        'The local analysis session has been closed.',
        'load',
      )
    }
  }

  private ensureWorker(): Worker {
    this.assertOpen()
    if (this.worker) return this.worker
    const worker = this.workerFactory(this.profileId)
    worker.addEventListener('message', this.handleMessage)
    worker.addEventListener('error', this.handleWorkerError)
    this.worker = worker
    return worker
  }

  private post(worker: Worker, message: WorkerRequest): void {
    worker.postMessage(message)
  }

  private stopWorker(reason: string): void {
    const worker = this.worker
    if (worker) {
      worker.removeEventListener(
        'message',
        this.handleMessage,
      )
      worker.removeEventListener(
        'error',
        this.handleWorkerError,
      )
      worker.terminate()
      this.worker = null
    }

    const loadError = cancelledError(reason, 'load')
    this.pendingLoad?.reject(loadError)
    this.pendingLoad = null
    this.loadPromise = null
    this.ready = null

    const inferenceError = cancelledError(reason, 'inference')
    for (const request of this.pending.values()) {
      request.reject(inferenceError)
    }
    this.pending.clear()
  }

  private resetArtifacts(): void {
    this.artifactAbortController?.abort()
    this.artifactAbortController = null
    this.matrixPromise = null
  }

  private embedText(
    worker: Worker,
    text: string,
  ): Promise<RawEmbedding> {
    const requestId = crypto.randomUUID()
    return new Promise<RawEmbedding>((resolve, reject) => {
      this.pending.set(requestId, {
        resolve,
        reject,
      })
      this.post(worker, {
        type: 'embed',
        profileId: this.profileId,
        requestId,
        text,
      })
    })
  }

  private searchFullMatrix(
    artifact: Awaited<ReturnType<EmbeddingClient['loadMatrix']>>,
    vector: Float32Array,
  ): SemanticRankingEntry[] {
    return searchVectorMatrix(
      artifact.matrix,
      vector,
      artifact.manifest.routeCardIds,
      artifact.matrix.count,
    )
  }

  private loadMatrix() {
    if (!this.matrixPromise) {
      this.artifactAbortController = new AbortController()
      const signal = this.artifactAbortController.signal
      const promise = (async () => {
        const response = await fetch(
          manifestUrl(this.profileId),
          { signal },
        )
        if (!response.ok) {
          throw new Error(
            `Reference manifest failed to load (${response.status}).`,
          )
        }
        const manifest =
          (await response.json()) as ArtifactManifest
        if (
          manifest.profileId !== this.profileId ||
          manifest.schemaVersion !== 1 ||
          !Array.isArray(manifest.routeCardIds) ||
          !manifest.routeCardIds.every(
            (routeCardId) => typeof routeCardId === 'string',
          ) ||
          !Number.isInteger(manifest.dimensions) ||
          !/^[a-f0-9]{64}$/.test(manifest.vectorSha256) ||
          !/^\/artifacts\/[^/?#]+\.bin$/.test(
            manifest.vectorUrl,
          )
        ) {
          throw new Error(
            'Reference manifest does not match the model.',
          )
        }
        const vectorResponse = await fetch(
          manifest.vectorUrl,
          { signal },
        )
        if (!vectorResponse.ok) {
          throw new Error(
            `Reference vectors failed to load (${vectorResponse.status}).`,
          )
        }
        const packedVectors =
          await vectorResponse.arrayBuffer()
        const measuredSha256 = await sha256Hex(packedVectors)
        if (measuredSha256 !== manifest.vectorSha256) {
          throw new Error(
            'Reference vector checksum does not match its manifest.',
          )
        }
        const matrix = decodeVectorMatrix(packedVectors)
        if (
          matrix.count !== manifest.routeCardIds.length ||
          matrix.dimensions !== manifest.dimensions
        ) {
          throw new Error(
            'Reference vectors do not match their manifest.',
          )
        }
        return {
          manifest,
          matrix,
        }
      })()
      this.matrixPromise = promise
      void promise.catch(() => {
        if (this.matrixPromise === promise) {
          this.matrixPromise = null
        }
      })
    }
    return this.matrixPromise
  }

  private readonly handleMessage = (
    event: MessageEvent<WorkerResponse>,
  ): void => {
    const message = event.data
    if (message.type === 'disposed') return
    if (
      'profileId' in message &&
      message.profileId !== this.profileId
    ) {
      return
    }
    if (message.type === 'progress') {
      if (message.loadId !== this.pendingLoad?.loadId) return
      this.onEvent({
        type: 'progress',
        progress: message.progress,
      })
      return
    }
    if (message.type === 'ready') {
      if (message.loadId !== this.pendingLoad?.loadId) return
      const ready = { device: message.device }
      this.ready = ready
      this.pendingLoad.resolve(ready)
      this.pendingLoad = null
      this.onEvent({
        type: 'ready',
        device: message.device,
      })
      return
    }
    if (message.type === 'fallback') {
      if (
        this.pendingLoad &&
        message.loadId !== this.pendingLoad.loadId
      ) {
        return
      }
      this.onEvent({
        type: 'fallback',
        failedDevice: message.failedDevice,
        nextDevice: message.nextDevice,
      })
      return
    }
    if (message.type === 'error') {
      const error = new EmbeddingClientError(message)
      const pendingLoad = this.pendingLoad
      if (
        message.phase === 'load' &&
        pendingLoad &&
        message.loadId === pendingLoad.loadId
      ) {
        pendingLoad.reject(error)
        this.pendingLoad = null
        this.loadPromise = null
        this.ready = null
        return
      }
      const request = message.requestId
        ? this.pending.get(message.requestId)
        : undefined
      if (request && message.requestId) {
        this.pending.delete(message.requestId)
        request.reject(error)
        return
      }
      for (const pending of this.pending.values()) {
        pending.reject(error)
      }
      this.pending.clear()
      return
    }

    const request = this.pending.get(message.requestId)
    if (!request) return
    this.pending.delete(message.requestId)
    request.resolve({
      device: message.device,
      vector: new Float32Array(message.vector),
    })
  }

  private readonly handleWorkerError = (
    event: ErrorEvent,
  ): void => {
    const message =
      event.message || 'The local embedding worker stopped.'
    const loadError = new EmbeddingClientError({
      code: 'WORKER_FAILED',
      phase: 'load',
      retryable: true,
      message,
    })
    this.pendingLoad?.reject(loadError)
    this.pendingLoad = null
    this.loadPromise = null
    this.ready = null

    const inferenceError = new EmbeddingClientError({
      code: 'WORKER_FAILED',
      phase: 'inference',
      retryable: true,
      message,
    })
    for (const request of this.pending.values()) {
      request.reject(inferenceError)
    }
    this.pending.clear()

    const failedWorker = this.worker
    if (failedWorker) {
      failedWorker.removeEventListener(
        'message',
        this.handleMessage,
      )
      failedWorker.removeEventListener(
        'error',
        this.handleWorkerError,
      )
      failedWorker.terminate()
      this.worker = null
    }
  }
}
